import type { DashboardConfig, RuntimeBrand } from "@webuild-ai/react-ui/types";
import type { Capabilities } from "@/hooks/useCapabilities";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useCapabilities } from "@/hooks/useCapabilities";
import { getDashboardConfig } from "@/config/dashboards";
import { brandIconSrc, isFolder } from "@/config/tenants";

import { type SidebarItem } from "./Sidebar";

/** Everything a menu rule may read when deciding on and building its entry. */
interface MenuContext {
  capabilities: Capabilities;
  brand: RuntimeBrand;
  t: (key: string) => string;
  dashboard: DashboardConfig | undefined;
}

/**
 * One sidebar entry: whether it appears, and how it is built once it does.
 * `build` is only called for a visible entry, so the order of translation calls
 * follows the order of the entries that appear.
 */
interface MenuRule {
  isVisible: (context: MenuContext) => boolean;
  build: (context: MenuContext) => SidebarItem;
}

/** A titled group of menu rules, omitted from the sidebar when none is visible. */
interface MenuSection {
  key: string;
  titleKey: string;
  rules: readonly MenuRule[];
}

const always = (): boolean => true;

/** A brand's copy override for a menu label, falling back when absent or empty. */
const menuCopy = (brand: RuntimeBrand, label: string, fallback: string): string =>
  brand.copy.menuItems?.[label] || fallback;

const isDashboardInSidebar = ({ dashboard }: MenuContext): boolean =>
  Boolean(dashboard?.showInSideBar && dashboard?.url);

const buildDashboardItem = ({ dashboard, brand, t }: MenuContext): SidebarItem => ({
  key: "dashboard",
  href: `/${dashboard?.urlSegment ?? "dashboard"}`,
  icon: "solar:chart-2-outline",
  img: brandIconSrc(brand.folder, "dashboard.svg"),
  title: dashboard?.title ? t(dashboard.title) : t("Dashboard"),
});

const OVERVIEW_RULES: readonly MenuRule[] = [
  // Dashboard comes first when it is configured to show in the sidebar.
  { isVisible: isDashboardInSidebar, build: buildDashboardItem },
  // Chat is available to every tenant.
  {
    isVisible: always,
    build: ({ brand, t }) => ({
      key: "chat",
      href: "/chat",
      icon: "solar:chat-line-linear",
      img: brandIconSrc(brand.folder, "pathfinder.svg"),
      title: t(menuCopy(brand, "Pathfinder", "Chat")),
    }),
  },
  // Library consolidates the old Workflows and Prompt Library entries and is
  // available to every tenant; per-source permissions are applied inside the page.
  {
    isVisible: always,
    build: ({ brand, t }) => ({
      key: "library",
      href: "/library",
      icon: "solar:notebook-bookmark-outline",
      img: brandIconSrc(brand.folder, "library.svg"),
      title: t(menuCopy(brand, "Library", "Library")),
    }),
  },
  // Workflow Builder reuses the Playground component set (LSEG-91) and — unlike
  // Playground below — is available to every tenant, including NGV and NGET. It
  // takes the authoring capability, not the `workflows` read flag: the Builder
  // saves, so a read-only role must not reach it.
  {
    isVisible: ({ capabilities }) => capabilities.canAuthorWorkflows,
    build: ({ brand, t }) => ({
      key: "workflow-builder",
      href: "/workflow-builder",
      icon: "solar:widget-2-linear",
      img: brandIconSrc(brand.folder, "workflow-builder.svg"),
      title: t("workflowBuilder.nav.title"),
    }),
  },
  // Automated Workflows (LSEG-107) sits directly after the Builder, since a
  // workflow is published in the Builder and then scheduled here. It shares the
  // Builder's gate, because scheduling a workflow and triggering a run are both
  // mutations rather than reads.
  {
    isVisible: ({ capabilities }) => capabilities.canAuthorWorkflows,
    build: ({ brand, t }) => ({
      key: "automations",
      href: "/automations",
      icon: "solar:clock-circle-linear",
      img: brandIconSrc(brand.folder, "workflow-automation.svg"),
      title: t("automations.nav.title"),
    }),
  },
  // Check server-validated permissions for each feature. Playground remains
  // restricted to the tenants it originally shipped for, now that Workflow
  // Builder covers NGV and NGET via the entry above.
  {
    isVisible: ({ capabilities, brand }) =>
      capabilities.canViewWorkflows && !isFolder(brand, "ngv") && !isFolder(brand, "nget"),
    build: ({ brand, t }) => ({
      key: "playground",
      href: "/workflows/playground",
      icon: "solar:layers-outline",
      img: brandIconSrc(brand.folder, "playground.svg"),
      title: t("Playground"),
    }),
  },
  {
    isVisible: ({ capabilities }) => capabilities.canViewKnowledgeHub,
    build: ({ brand, t }) => ({
      key: "data",
      href: "/datahub",
      icon: "solar:database-linear",
      img: brandIconSrc(brand.folder, "knowledge.svg"),
      title: t(menuCopy(brand, "Knowledge Hub", "Knowledge Hub")),
    }),
  },
];

const SETTINGS_RULES: readonly MenuRule[] = [
  {
    isVisible: ({ capabilities }) => capabilities.canViewMonitoring,
    build: ({ brand, t }) => ({
      key: "monitoring",
      href: "/metrics",
      title: t("Monitoring"),
      icon: "solar:pie-chart-2-outline",
      img: brandIconSrc(brand.folder, "solutions.svg"),
    }),
  },
  // Compliance dashboard depends on ChatGov (AWS-only). Hide the entry
  // for non-webuild tenants so Azure-only deployments don't see a broken page.
  {
    isVisible: ({ capabilities, brand }) =>
      capabilities.canViewMonitoring && isFolder(brand, "webuild"),
    build: ({ t }) => ({
      key: "compliance",
      href: "/compliance",
      title: t("Compliance Check"),
      icon: "solar:eye-scan-outline",
      img: "/brands/webuild/compliance.svg",
    }),
  },
];

// Admin page is accessed via the profile dropdown menu, not the sidebar
const MENU_SECTIONS: readonly MenuSection[] = [
  { key: "overview", titleKey: "Overview", rules: OVERVIEW_RULES },
  { key: "settings", titleKey: "Settings", rules: SETTINGS_RULES },
];

/**
 * Pure function that builds menu items from capabilities and brand config.
 * Extracted so it can be tested without renderHook (Vitest 4.x jsdom constraint).
 *
 * Every section's entries are built before any section title is translated,
 * which keeps the order of translation calls stable.
 */
export const buildSecureMenuItems = (
  capabilities: Capabilities,
  brand: RuntimeBrand,
  t: (key: string) => string,
): SidebarItem[] => {
  const context: MenuContext = {
    capabilities,
    brand,
    t,
    dashboard: getDashboardConfig(brand.folder),
  };
  const built = MENU_SECTIONS.map((section) => ({
    section,
    items: section.rules
      .filter((rule) => rule.isVisible(context))
      .map((rule) => rule.build(context)),
  }));

  return built
    .filter(({ items }) => items.length > 0)
    .map(({ section, items }) => ({ key: section.key, title: t(section.titleKey), items }));
};

/**
 * Sidebar menu items hook, driven by the shared capability layer so the sidebar
 * cannot disagree with the landing tiles or the route guards about what a user
 * may reach.
 *
 * `isValidating` reports whether the RBAC grants have settled. The
 * `SecurePermissions` flags are already resolved by the time any route renders,
 * because `PrivateRoute` holds the app on the loading page until the
 * `verify-permissions` call returns.
 */
export const useSidebarMenuItems = (): {
  menuItems: SidebarItem[];
  isValidating: boolean;
} => {
  const capabilities = useCapabilities();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    setMenuItems(buildSecureMenuItems(capabilities, brand, t));
  }, [capabilities, brand, t]);

  return {
    menuItems,
    isValidating: !capabilities.isSettled,
  };
};
